import type { ShouldRevalidateFunction } from 'react-router';
import type { LoaderFunctionArgs } from '~/types/route-args';
import { fetchSimilarProjectsPage } from '@modules/project/util/fetch-similar-projects.server';
import { SIMILAR_PROJECTS_PAGE_SIZE } from '@modules/project/util/similar-projects-config';

/** Resource-route path for the project page's Similar Projects "Load More". */
export const LOAD_MORE_SIMILAR_PROJECTS_ACTION =
    '/actions/load-more-similar-projects';

/** Max projects per request — guards against manipulated `limit` values. */
const MAX_LIMIT = 50;

/**
 * Deepest page this will serve. The search index's own result window is the
 * real ceiling (10000 by default) and a request past it returns nothing, so
 * refusing here keeps an unbounded `offset` from turning into a run of
 * ever-more-expensive deep-paging queries against the whole category.
 */
const MAX_OFFSET = 10000;

/** Only `/projects` plus alphanumeric-and-hyphen segments; no traversal. */
const CATEGORY_LINK_PATTERN = /^\/projects(\/[\w-]+)*\/?$/;

const EMPTY = { projects: [], hasMore: false, totalCount: 0 };

/**
 * Later pages of the project page's Similar Projects grid — the same
 * same-category search the loader runs for page 0, so offsets line up and the
 * ordering continues rather than restarting.
 *
 * Query params: `categoryLink`, `currentProjectId`, `offset`, `limit`.
 *
 * `offset` is read as a page boundary — the search service pages by index, so
 * an offset that is not a multiple of `limit` is floored to the page holding
 * it. Callers step by whole pages, which is what keeps the two aligned.
 */
export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);

    const categoryLink = url.searchParams.get('categoryLink') ?? '';
    const offsetParam = url.searchParams.get('offset');
    const limitParam = url.searchParams.get('limit');
    const currentProjectId = Number(
        url.searchParams.get('currentProjectId') ?? 0,
    );

    if (
        !categoryLink.startsWith('/projects') ||
        categoryLink.includes('..') ||
        !CATEGORY_LINK_PATTERN.test(categoryLink)
    ) {
        logger.warning('Invalid categoryLink in load-more-similar-projects', {
            categoryLink,
        });
        return EMPTY;
    }

    const offset = offsetParam ? parseInt(offsetParam, 10) : 0;
    const limit = limitParam
        ? parseInt(limitParam, 10)
        : SIMILAR_PROJECTS_PAGE_SIZE.desktop;
    if (
        !Number.isInteger(offset) ||
        offset < 0 ||
        offset > MAX_OFFSET ||
        !Number.isInteger(limit) ||
        limit < 1 ||
        limit > MAX_LIMIT
    ) {
        logger.warning('Invalid offset/limit in load-more-similar-projects', {
            offsetParam,
            limitParam,
        });
        return EMPTY;
    }

    try {
        return await fetchSimilarProjectsPage({
            context,
            categoryLink,
            currentProjectId: Number.isInteger(currentProjectId)
                ? currentProjectId
                : 0,
            offset,
            limit,
        });
    } catch (error) {
        logger.error('Error in /actions/load-more-similar-projects', {
            error,
            categoryLink,
            offset,
            limit,
        });
        // An empty page at 200, like the invalid-input paths above. A non-2xx
        // from a fetcher.load is thrown as an ErrorResponse and caught by the
        // nearest boundary, so returning 500 would replace the whole project
        // page over a grid that could simply stop growing.
        return EMPTY;
    }
};

/** Fetched on demand — navigation must not re-run it. */
export const shouldRevalidate: ShouldRevalidateFunction = () => false;
